import { useCloseUnlessOrgNetwork } from '../hooks/useCloseWhenMissing'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import MuiLink from '@mui/material/Link'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconNode from './IconNode'
import Modal from './Modal'
import CreateRefusal from './CreateRefusal'
import Timestamp from './Timestamp'
import { useModalClose } from '../hooks/useModalClose'
import { useOrgRole } from '../hooks/useOrgRole'
import { getUserId } from '../lib/workos'
import { mayAddNodes, type OrgRole } from '../utilities/orgRole'
import useBilling from '../models/useBilling'
import useNetwork from '../models/useNetwork'
import useNodeTasks from '../models/useNodeTasks'
import useTasks from '../models/useTasks'
import useUsage, { currentPeriod } from '../models/useUsage'
import { callTool } from '../lib/tools'
import { supabase } from '../supabaseClient'
import { DOCS_PROVISIONING_URL, REGEX_VALID_CHARACTERS } from '../constants'
import { limitRefusal, type LimitRefusal } from '../utilities/limitRefusal'
import { planDefaultHours, planMaxHours } from '../utilities/taskDeadline'
import {
  defaultStopAfter,
  dialogSizes,
  includedComputeLine,
  normalizeNodeName,
  priceLabel,
  provisionArgs,
  sizeShapeLabel,
  isNetworkAccess,
  KEEP_PRIVATE_LABEL,
  keepPrivateCaption,
  startingAccessArg,
  startingAccessResultLine,
  stopAfterOptions,
  validateComputeNodeForm,
  type ComputeNodeForm,
  type NetworkAccess,
} from '../utilities/computeNodePage'

/** The `provision_node` payload, as far as this dialog reads it (`ProvisionNodeResponse`). */
type ProvisionResult = {
  task_id: string
  /** The network the router used. Anything that follows up on the node uses this id, not the name. */
  network_id: string
  network_name: string
  node_name: string
  class: string
  deadline_at?: string
  readopted_node_id?: string
}

/**
 * New compute node (nobgp/app#330): start a provisioned machine from the app.
 *
 * ⚠⚠ THE ROUTER DECIDES, THE DIALOG ASKS. `provision_node` runs with the user's
 * own session, so the router applies the same checks as over MCP — provisioning
 * access, the node and compute allowances, the credit balance and the deadline
 * rules. The dialog validates the form only to save a round trip, and it shows
 * the router's refusal in the router's words.
 *
 * ⚠⚠ THE NETWORK GOES BY ID. A name can exist in two of the user's organizations,
 * and the router resolves a name to the personal organization first. The dialog
 * sends `network_id` only (nobgp-router#1480), so the machine starts in the network
 * on screen (`provisionArgs`).
 */
export default function ModalComputeNode({ inline }: Readonly<{ inline?: boolean }>) {
  const { networkId } = useParams()
  // ⚠ An id in the path that is not an object of the active org sends the reader home (app#475).
  useCloseUnlessOrgNetwork(networkId)
  const { open, handleClose } = useModalClose(networkId ? `/networks/${networkId}` : '/', inline)
  const networks = useNetwork(s => s.networks)
  const catalogue = useNodeTasks(s => s.catalogue)
  const getCatalogue = useNodeTasks(s => s.getCatalogue)
  const plan = useBilling(s => s.plan)
  const plans = useBilling(s => s.plans)
  // The same axis the usage page's Compute bar reads, so a promotion counts.
  const computeAxis = useUsage(s => s.axes.find(a => a.axis === 'compute'))
  // ⚠ The usage store's period is SHARED with /usage. A reader who picked a past month there
  // leaves that month's axis here, and "left this month" read from it is false. So the line
  // shows only for the current period. The dialog does not move the /usage selection.
  const usagePeriod = useUsage(s => s.period)
  const loadUsage = useUsage(s => s.get)
  const { can, role } = useOrgRole()
  const canCreate = can('node.write')
  // ⚠ "Members may add nodes" (docs/PERMISSIONS.md § 5 Attachment, app#376): a network whose
  // setting is off is offered only to its owner or an org Owner/Admin. The router's `RequireAttach`
  // refuses the others; this only keeps the picker from offering a network that will be refused.
  const mayAddTo = (n: NetworkRecord) => mayAddNodes(role as OrgRole, n.ownerId, getUserId(), n.membersMayAddNodes)
  // ⚠ Read off the plan row, the same column the router decides on. See ProvisionedTasks.
  const allowNoDeadline = plan?.computeDeadlineOptional === true
  const defaultHours = planDefaultHours(plan)
  const maxHours = planMaxHours(plan)

  const fixedNetwork = networks.find(n => n.id === networkId)
  // ⚠ The network is chosen by ID: two networks in one organization may share a name.
  const [chosenId, setChosenId] = useState<string>(networkId ?? '')
  const [form, setForm] = useState<Omit<ComputeNodeForm, 'networkId'>>({
    name: '',
    size: 'small',
    // ⚠ Empty until the reader picks: the plan loads after the dialog can open, so the default
    // entry is resolved at render (`stopAfter` below), not frozen here.
    stopAfter: '',
    // The new node follows its network unless the reader keeps it private (D-20, app#395).
    keepPrivate: false,
  })
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  // ⚠⚠ A REF, NOT ONLY THE STATE. Enter in the Name field calls `submit` directly, and a second
  // Enter before React re-renders still sees `sending` false. Two `provision_node` calls start
  // two machines that both bill: the router gives the second one a suffixed name.
  const inFlight = useRef(false)
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)
  const [result, setResult] = useState<ProvisionResult | null>(null)
  // True when the call that produced `result` sent `access: private`. The result line reads it,
  // not the box, which the reader cannot change after the call.
  const [sentPrivate, setSentPrivate] = useState(false)

  useEffect(() => {
    void getCatalogue()
    void loadUsage()
  }, [getCatalogue, loadUsage])

  // The network store can land after the dialog opens (a hard refresh on this route).
  // ⚠ The id from the URL is not trusted: it can name a network this reader does not have.
  // Then the dialog showed "Network: " with no name and a hidden "Select a network" error.
  // ⚠ Since app#475 a network that is not a network of the active org closes the dialog to the
  // home view (`useCloseUnlessOrgNetwork` above), after one read that says so. This fallback
  // still serves the time before that answer, and a read that failed.
  // ⚠ Nothing is reset while the store is empty, or a hard refresh would lose the URL's network.
  useEffect(() => {
    if (networks.length === 0) return
    // ⚠ The one network is chosen for the reader only when they may add a node to it (app#376).
    if (!networks.some(n => n.id === chosenId)) {
      setChosenId(networks.length === 1 && mayAddTo(networks[0]) ? (networks[0].id ?? '') : '')
    }
    // `role` is here because `mayAddTo` reads it: the role can land after the networks.
  }, [chosenId, networks, role])
  // ⚠ A network the reader may not add a node to is never `chosen` (app#376): not the one network
  // of the org, and not the network in the URL. The picker then shows, with that network disabled
  // and its reason, instead of a "Network: X" line whose submit the router refuses.
  const chosen = networks.find(n => n.id === chosenId && mayAddTo(n))

  // The chosen network's access, Org or Private (you and org admins): `networks.access` (router
  // migration 0219, D-20). ⚠ Its own select, never a column of `useNetwork.get()`: a database
  // without the column fails this read alone, and the dialog still works. null: not known.
  const [networkAccess, setNetworkAccess] = useState<NetworkAccess | null>(null)
  const chosenNetworkId = chosen?.id
  useEffect(() => {
    let live = true
    setNetworkAccess(null)
    if (!chosenNetworkId) return
    void (async () => {
      const { data, error } = await supabase
        .from('networks')
        .select('access')
        .eq('id', chosenNetworkId)
        .maybeSingle()
      if (!live) return
      if (error) console.warn('networks.access', error)
      setNetworkAccess(isNetworkAccess(data?.access) ? data.access : null)
    })()
    return () => {
      live = false
    }
  }, [chosenNetworkId])

  const stopAfter = form.stopAfter || defaultStopAfter(defaultHours)
  const full: ComputeNodeForm = { ...form, stopAfter, networkId: chosen?.id ?? '', networkAccess }

  const sizes = useMemo(() => dialogSizes(catalogue), [catalogue])
  const options = useMemo(
    () => stopAfterOptions(allowNoDeadline, plans, defaultHours, maxHours),
    [allowNoDeadline, plans, defaultHours, maxHours],
  )
  const errors = validateComputeNodeForm(full, sizes, options)
  const valid = Object.keys(errors).length === 0
  const included = usagePeriod === currentPeriod() ? includedComputeLine(plan, computeAxis) : null
  const shown = (field: keyof ComputeNodeForm) => (submitted ? errors[field] : undefined)

  const submit = async () => {
    if (inFlight.current || sending) return
    setSubmitted(true)
    setRefusal(null)
    if (!valid || !canCreate) return
    inFlight.current = true
    setSending(true)
    try {
      const res = await callTool<ProvisionResult>('provision_node', provisionArgs(full, options))
      setSentPrivate(startingAccessArg(full.keepPrivate).access === 'private')
      setResult(res)
      // The usage screen's machine list, if it is open in another view.
      void useTasks.getState().get()
    } catch (e) {
      // ⚠ The router's sentence, verbatim. A spent allowance also carries an Upgrade action.
      console.error('provision_node failed', e)
      setRefusal(limitRefusal(e) ?? { message: e instanceof Error ? e.message : String(e) })
    } finally {
      inFlight.current = false
      setSending(false)
    }
  }

  const requested = normalizeNodeName(form.name)
  const resultAccessLine = result ? startingAccessResultLine(sentPrivate, result.readopted_node_id) : null

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="sm"
      onClose={inline ? undefined : handleClose}
      color="node.main"
      icon={<IconNode type="node" width={54} height={54} outline plus />}
    >
      <DialogContent>
        <Typography variant="subtitle1" color="primary" sx={{ mb: 3 }}>
          New compute node
        </Typography>

        {result ? (
          <Stack spacing={1.5}>
            <Typography variant="body1">
              <strong>{result.node_name}</strong> is starting. It joins {result.network_name} in about a
              minute.
            </Typography>
            {result.node_name !== requested && (
              <Alert severity="info">
                The name {requested} is in use by a running node, so this machine is called{' '}
                <strong>{result.node_name}</strong>.
              </Alert>
            )}
            {result.readopted_node_id && (
              <Typography variant="body2">
                This brings back the existing node {result.node_name}, with its labels, permissions and
                node storage.
              </Typography>
            )}
            {resultAccessLine && <Typography variant="body2">{resultAccessLine}</Typography>}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {result.deadline_at ? (
                <>
                  It stops on <Timestamp date={new Date(result.deadline_at)} variant="full" />. You can
                  extend the deadline from the node page.
                </>
              ) : (
                'It has no time limit. It runs, and bills, until you terminate it.'
              )}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />

            {/* ⚠ The select shows whenever no valid network is chosen, so its error is never hidden. */}
            {(!chosen || (!fixedNetwork && networks.length !== 1)) && (
              <FormControl size="small" error={!!shown('networkId')}>
                <InputLabel id="compute-network">Network</InputLabel>
                <Select
                  labelId="compute-network"
                  label="Network"
                  value={chosenId}
                  onChange={e => setChosenId(e.target.value)}
                >
                  {networks.map(n => (
                    <MenuItem key={n.id} value={n.id} disabled={!mayAddTo(n)}>
                      {n.name}
                      {mayAddTo(n) ? '' : ' (only its owner or an admin adds nodes)'}
                    </MenuItem>
                  ))}
                </Select>
                {shown('networkId') && <FormHelperText>{shown('networkId')}</FormHelperText>}
              </FormControl>
            )}
            {chosen && (fixedNetwork || networks.length === 1) && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Network: {chosen.name}
              </Typography>
            )}

            <TextField
              autoFocus
              size="small"
              label="Name"
              placeholder="worker-1"
              value={form.name}
              onChange={e => {
                const value = e.target.value.toLowerCase()
                if (REGEX_VALID_CHARACTERS.test(value)) setForm(f => ({ ...f, name: value }))
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') void submit()
              }}
              error={!!shown('name')}
              helperText={shown('name')}
            />

            <FormControl error={!!shown('size')}>
              <FormLabel id="compute-size">Size</FormLabel>
              {sizes.length === 0 ? (
                <FormHelperText>Loading sizes…</FormHelperText>
              ) : (
                <RadioGroup
                  aria-labelledby="compute-size"
                  value={form.size}
                  onChange={e => setForm(f => ({ ...f, size: e.target.value }))}
                >
                  {sizes.map(s => (
                    <FormControlLabel
                      key={s.name}
                      value={s.name}
                      control={<Radio size="small" />}
                      label={
                        <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
                          <Typography variant="body2" sx={{ minWidth: 56 }}>
                            {s.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 140 }}>
                            {sizeShapeLabel(s)}
                          </Typography>
                          <Typography variant="body2">{priceLabel(s.priceCentsPerHour)}</Typography>
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
              {shown('size') && <FormHelperText>{shown('size')}</FormHelperText>}
            </FormControl>

            <FormControl size="small" error={!!shown('stopAfter')}>
              <InputLabel id="compute-stop-after">Stop after</InputLabel>
              <Select
                labelId="compute-stop-after"
                label="Stop after"
                value={stopAfter}
                onChange={e => setForm(f => ({ ...f, stopAfter: e.target.value }))}
              >
                {options.map(o => (
                  <MenuItem key={o.key} value={o.key} disabled={o.disabled}>
                    {o.label}
                    {o.hint && (
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        · {o.hint}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {stopAfter === 'none'
                  ? 'Nothing stops this machine. It runs, and bills, until you terminate it.'
                  : 'At the deadline the container and its disk are deleted. The node, its name and its node storage stay.'}
              </FormHelperText>
            </FormControl>

            {/* The new node's STARTING setting (router PERMISSIONS.md § 4.1, D-20, D-25): it follows its
                network, or the reader keeps it Private (you and org admins). Written ONCE, on the node
                the machine creates. A re-adopted node keeps its own. The box is on in a Private network
                too: a node set to Private stays private when the network changes to Org. */}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ paddingTop: 0.25 }}
                  checked={form.keepPrivate === true}
                  onChange={e => setForm(f => ({ ...f, keepPrivate: e.target.checked }))}
                />
              }
              label={
                <>
                  <Typography variant="body2">{KEEP_PRIVATE_LABEL}</Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {keepPrivateCaption(form.keepPrivate === true, networkAccess)} You can change this later.
                  </Typography>
                </>
              }
            />

            <Typography variant="body2">
              Image: Ubuntu 24.04 (noBGP compute image) ·{' '}
              <MuiLink href={DOCS_PROVISIONING_URL} target="_blank" rel="noopener noreferrer">
                What is installed?
              </MuiLink>
            </Typography>

            {included && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {included}
              </Typography>
            )}

            {!canCreate && (
              <Alert severity="info">Your role in this organization cannot start machines.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            {chosenId && (
              <Button component={Link} to={`/networks/${chosenId}`} variant="text">
                Open network
              </Button>
            )}
            <Button variant="contained" onClick={() => void handleClose()}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button variant="text" sx={{ color: 'GrayText' }} onClick={() => void handleClose()}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={sending || !canCreate || (submitted && !valid)}
              onClick={() => void submit()}
            >
              {sending ? 'Creating…' : 'Create'}
            </Button>
          </>
        )}
      </DialogActions>
    </Modal>
  )
}
